import React from 'react';
import { useParams } from 'react-router-dom';
import { useCrmDashboard } from './dashboard/useCrmDashboard';
import { CrmAuthGate } from './dashboard/CrmAuthGate';
import { CrmHeader } from './dashboard/CrmHeader';
import { CrmCallLogsTab } from './dashboard/CrmCallLogsTab';
import { CrmChatTab } from './dashboard/CrmChatTab';
import { CrmCdrTab } from './dashboard/CrmCdrTab';
import { CrmVoicemailsTab } from './dashboard/CrmVoicemailsTab';
import { CrmSettingsDrawer } from './dashboard/CrmSettingsDrawer';
import { CrmFloatingOverlays } from './dashboard/CrmFloatingOverlays';
import { ContactEditorModal, AddressBookManagerModal } from './AddressBookModals';

export default function CRMDashboard() {
  const { slug } = useParams<{ slug: string }>();
  const vm = useCrmDashboard(slug);

  if (vm.requiresAuth || vm.error || !vm.data) {
    return (
      <CrmAuthGate
        requiresAuth={vm.requiresAuth}
        crmRequiresPassword={vm.crmRequiresPassword}
        crmName={vm.crmName}
        authName={vm.authName}
        setAuthName={vm.setAuthName}
        authInput={vm.authInput}
        setAuthInput={vm.setAuthInput}
        authError={vm.authError}
        handleAuthSubmit={vm.handleAuthSubmit}
        error={vm.error}
        hasData={!!vm.data}
      />
    );
  }

  const { crm, logs } = vm.data;
  const maxRows = vm.clientMaxRows !== null ? vm.clientMaxRows : (crm?.maxRows || 10);
  const resolvedTheme = vm.clientTheme || crm?.theme || 'dark';
  const isLight = resolvedTheme === 'light';

  const handleEditContact = (phone: string, name: string) => {
    vm.setContactEditorModalOpen({ open: true, defaultData: { name }, initialPhoneNumber: phone });
  };

  const handleResetDefaults = () => {
    localStorage.removeItem('crm-max-rows');
    localStorage.removeItem('crm-hot-window');
    localStorage.removeItem('crm-tz');
    localStorage.removeItem('crm-theme');
    window.dispatchEvent(new Event('storage'));
    localStorage.removeItem('crm-hot-call-toasts');
    vm.setClientMaxRows(null);
    vm.setClientHotCallWindow(null);
    vm.setClientTimezone('local');
    vm.setClientTheme(null);
    vm.setClientHotCallToastsEnabled(true);
  };

  return (
    <div
      className={`crm-page-container ${vm.activeTab === 'chat' ? 'chat-active' : ''}`}
      style={{
        height: vm.activeTab === 'chat' ? '100dvh' : undefined,
        minHeight: vm.activeTab === 'chat' ? undefined : '100dvh',
        background: isLight ? '#f8fafc' : 'transparent',
        color: isLight ? '#09090b' : 'white',
        fontFamily: 'system-ui, sans-serif',
        padding: '1.5rem',
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        transition: 'background 0.3s, color 0.3s',
        overflow: vm.activeTab === 'chat' ? 'hidden' : undefined,
        boxSizing: 'border-box',
        overscrollBehaviorY: 'none'
      }}
      onTouchStart={(e) => vm.setTouchStartY(e.touches[0].clientY)}
      onTouchEnd={(e) => {
        if (vm.touchStartY === null) return;
        const diff = e.changedTouches[0].clientY - vm.touchStartY;
        if (diff > 50 && vm.touchStartY < 100 && !vm.showHeaderMobile) {
          vm.setShowHeaderMobile(true);
        } else if (diff < -50 && vm.showHeaderMobile && vm.touchStartY < 250) {
          vm.setShowHeaderMobile(false);
        }
        vm.setTouchStartY(null);
      }}
    >
      <CrmSettingsDrawer
        isOpen={vm.isDrawerOpen}
        onClose={() => vm.setIsDrawerOpen(false)}
        isLight={isLight}
        settingsTab={vm.settingsTab}
        setSettingsTab={vm.setSettingsTab}
        setClientTheme={vm.setClientTheme}
        clientTimezone={vm.clientTimezone}
        setClientTimezone={vm.setClientTimezone}
        pushStatus={vm.pushStatus}
        isPushLoading={vm.isPushLoading}
        subscribeToWebPush={vm.subscribeToWebPush}
        handleTestPushNotification={vm.handleTestPushNotification}
        slug={slug}
        onResetDefaults={handleResetDefaults}
      />

      <div
        style={{
          width: '100%',
          maxWidth: '900px',
          display: 'flex',
          flexDirection: 'column',
          gap: '1.5rem',
          flex: 1,
          minHeight: 0,
          overflow: vm.activeTab === 'chat' ? 'hidden' : undefined
        }}
      >
        <CrmHeader
          crm={crm}
          isLight={isLight}
          showHeaderMobile={vm.showHeaderMobile}
          setShowHeaderMobile={vm.setShowHeaderMobile}
          setShowLargeQr={vm.setShowLargeQr}
          pushStatus={vm.pushStatus}
          subscribeToWebPush={vm.subscribeToWebPush}
          setIsDrawerOpen={vm.setIsDrawerOpen}
          setSettingsTab={vm.setSettingsTab}
          activeTab={vm.activeTab}
          setActiveTab={vm.setActiveTab}
          hotCallsCount={vm.hotCallsCount}
          unreadMessages={vm.unreadMessages}
          setAddressBookModalOpen={vm.setAddressBookModalOpen}
        />

        <CrmCallLogsTab
          activeTab={vm.activeTab}
          isLight={isLight}
          crm={crm}
          logs={logs}
          page={vm.page}
          setPage={vm.setPage}
          maxRows={maxRows}
          hotCallWindow={vm.hotCallWindow}
          activeAgents={vm.activeAgents}
          now={vm.now}
          timeSkew={vm.timeSkew}
          scrollRef={vm.scrollRef}
          setShowAgentModal={vm.setShowAgentModal}
          setCallLogsSettingsOpen={vm.setCallLogsSettingsOpen}
          setChatTrigger={vm.setChatTrigger}
          defaultChatDid={vm.defaultChatDid}
          setActiveTab={vm.setActiveTab}
          setShowHeaderMobile={vm.setShowHeaderMobile}
          clientTimezone={vm.clientTimezone}
          onEditContact={handleEditContact}
        />

        <CrmChatTab
          activeTab={vm.activeTab}
          isLight={isLight}
          crm={crm}
          updateCrmSettings={vm.updateCrmSettings}
          chatDids={vm.chatDids}
          defaultDid={vm.defaultChatDid}
          chatFolders={vm.chatFolders}
          fetchConversations={vm.fetchConversations}
          fetchUnreadCounts={vm.fetchUnreadCounts}
          fetchThreadMessages={vm.fetchThreadMessages}
          fetchSearchResults={vm.fetchSearchResults}
          fetchExportMessages={vm.fetchExportMessages}
          sendChatMessage={vm.sendChatMessage}
          deleteConversations={vm.deleteConversations}
          moveConversations={vm.moveConversations}
          manageCustomFolder={vm.manageCustomFolder}
          sendChatEvent={vm.sendChatEvent}
          resolveCnamMap={vm.resolveCnamMap}
          uploadChatMedia={vm.uploadChatMedia}
          markMessagesAsRead={vm.markMessagesAsRead}
          markMessagesAsUnread={vm.markMessagesAsUnread}
          slug={slug}
          authName={vm.authName}
          authInput={vm.authInput}
          setUnreadMessages={vm.setUnreadMessages}
          chatTrigger={vm.chatTrigger}
          cnamVersion={vm.cnamRefreshTrigger}
          onEditContact={handleEditContact}
        />

        <CrmCdrTab
          activeTab={vm.activeTab}
          isLight={isLight}
          slug={slug}
          crm={crm}
          serverConfig={vm.data?.serverConfig}
          resolveCnamMap={vm.resolveCnamMap}
          cdrAuthed={vm.cdrAuthed}
          setCdrAuthed={vm.setCdrAuthed}
          cdrPasswordInput={vm.cdrPasswordInput}
          setCdrPasswordInput={vm.setCdrPasswordInput}
          cdrAuthError={vm.cdrAuthError}
          setCdrAuthError={vm.setCdrAuthError}
        />

        <CrmVoicemailsTab
          activeTab={vm.activeTab}
          isLight={isLight}
          slug={slug}
          enableVoicemails={crm?.enableVoicemails}
          voicemailsRequirePin={crm?.voicemailsRequirePin}
          freepbxServerId={crm?.freepbxServerId}
        />
      </div>

      <ContactEditorModal
        open={vm.contactEditorModalOpen.open}
        contactId={vm.contactEditorModalOpen.contactId}
        initialPhoneNumber={vm.contactEditorModalOpen.initialPhoneNumber}
        defaultData={vm.contactEditorModalOpen.defaultData}
        onClose={(reload?: boolean) => {
          vm.setContactEditorModalOpen({ open: false });
          if (reload) {
            vm.setCnamRefreshTrigger(p => p + 1);
            vm.setRefreshTrigger(p => p + 1);
          }
        }}
        isLight={isLight}
        slug={slug}
      />

      <AddressBookManagerModal
        open={vm.addressBookModalOpen}
        onClose={(reload?: boolean) => {
          vm.setAddressBookModalOpen(false);
          if (reload) {
            vm.setCnamRefreshTrigger(p => p + 1);
            vm.setRefreshTrigger(p => p + 1);
          }
        }}
        isLight={isLight}
        slug={slug}
      />

      <CrmFloatingOverlays
        showLargeQr={vm.showLargeQr}
        setShowLargeQr={vm.setShowLargeQr}
        showAgentModal={vm.showAgentModal}
        setShowAgentModal={vm.setShowAgentModal}
        callLogsSettingsOpen={vm.callLogsSettingsOpen}
        setCallLogsSettingsOpen={vm.setCallLogsSettingsOpen}
        showHeaderMobile={vm.showHeaderMobile}
        setShowHeaderMobile={vm.setShowHeaderMobile}
        toastQueue={vm.toastQueue}
        setToastQueue={vm.setToastQueue}
        isLight={isLight}
        crm={crm}
        activeAgents={vm.activeAgents}
        now={vm.now}
        timeSkew={vm.timeSkew}
        clientMaxRows={vm.clientMaxRows}
        setClientMaxRows={vm.setClientMaxRows}
        clientHotCallWindow={vm.clientHotCallWindow}
        setClientHotCallWindow={vm.setClientHotCallWindow}
        clientHotCallToastsEnabled={vm.clientHotCallToastsEnabled}
        setClientHotCallToastsEnabled={vm.setClientHotCallToastsEnabled}
        defaultChatDid={vm.defaultChatDid}
        setActiveTab={vm.setActiveTab}
        setChatTrigger={vm.setChatTrigger}
        hotCallWindow={vm.hotCallWindow}
        onEditContact={handleEditContact}
      />
    </div>
  );
}
